import * as React from 'react';
import { Link } from '@dx-ui/osc-link';
import { DropdownMenu } from 'radix-ui';
import cx from 'classnames';
import { useTranslation } from 'next-i18next';
import { LoginForm } from '@dx-ui/osc-login';
import Icon from '@dx-ui/osc-icon';
import { LanguageSelector } from '@dx-ui/osc-language-selector';
import type { HeaderProps, LinkWithExperimentationConfiguration } from './types';
import { BrandHeaderBody } from './brand-header-body/brand-header-body';
import { sendInteractionReward } from './header.utilities';
import { sendReward } from '@dx-ui/framework-conductrics';
import { HeaderLogoLink } from './header.logo';
import { useRect } from '@dx-ui/utilities-use-rect';
import { useRouter } from 'next/router';
import { getLocaleFormattedNumber } from '@dx-ui/framework-i18n';
import { useFilterMegaNavExperiments } from './hooks/use-filter-mega-nav-experiments';
import { Button } from '@dx-ui/global-ui-prism';
import { NavItemWithModal } from './brand-header-body/nav-items';

const HeaderMenuLink: React.FunctionComponent<
  React.PropsWithChildren<LinkWithExperimentationConfiguration>
> = (link) => {
  const [t] = useTranslation('osc-header');
  return (
    <DropdownMenu.Item asChild={true}>
      <a
        href={link.url}
        onClick={link.onClick}
        className={cx(
          'block px-2 py-3 text-surface hover:text-surface-muted highlighted:bg-surface-legacy',
          link.className
        )}
        {...(link.adaDescription && {
          'aria-label': `${link.adaDescription}${link.isNewWindow ? `, ${t('newTab')}` : ''}`,
        })}
        {...(link.isNewWindow && { target: '_blank', rel: 'noopener noreferrer' })}
        data-conductrics-goal={link.experimentationConfiguration?.goal}
        data-conductrics-value={link.experimentationConfiguration?.value}
      >
        {link.label}
        {link.isNewWindow && !link.adaDescription ? (
          <span className="sr-only">
            , <span>{t('newTab')}</span>
          </span>
        ) : null}
      </a>
    </DropdownMenu.Item>
  );
};

export const Header = ({
  ref: forwardedRef,
  megaNavLinks,
  brand,
  isFluid = true,
  mainNavLinks,
  onSignOut,
  onSignInAttempt,
  suppressLogo = false,
  shouldSuppressHonorsAccountLinks,
  shouldSuppressFindStay,
  shouldSuppressJoinAndSignIn,
  shouldOpenAccountLinksInNewWindow,
  shouldOpenJoinInNewWindow,
  shouldOpenJoinInPage,
  user,
  userLinks,
  loginOptions,
  children,
  languageSelectorOptions,
  showNewHeader = true,
  heading,
  wrapperId = '__next',
  mvts,
  surfaceType = 'base',
  headerVariantType = 'default',
  globalNavLinks,
  isTranslucent,
  onGlobalNavigationClick,
  isLegacyGlobalNavigationClickAnalyticsEnabled = true,
  analytics,
  ...rest
}: HeaderProps) => {
  const [t] = useTranslation(['osc-header', 'osc-login']);
  const defaultOptions = {
    onLoginAttempt: onSignInAttempt,
    sourceId: 'OHW' as const,
    analytics,
    isLegacySignInAnalyticsEnabled: analytics?.isLegacySignInAnalyticsEnabled,
  };

  const globalNavLinksFiltered = useFilterMegaNavExperiments(globalNavLinks);

  //TODO remove this manual memo later once compiler is enabled on this component
  const modifiedUserLinks = React.useMemo(() => {
    if (!userLinks?.signUpLink) return userLinks;

    return {
      ...userLinks,
    };
  }, [userLinks]);

  const commonHeaderBodyProps = {
    brand,
    isFluid,
    mainNavLinks,
    onSignOut,
    onSignInAttempt,
    suppressLogo,
    user,
    userLinks: modifiedUserLinks,
    loginOptions,
    children,
    heading,
    shouldOpenJoinInPage,
    analytics,
  };

  return (
    <>
      <a href="#skipped" className="btn--lg btn--flat sr-only focus:not-sr-only">
        {t('skipToContent')}
      </a>
      <header
        className="header-bottom-border border-solid"
        data-testid="shared-header"
        ref={forwardedRef}
        {...rest}
      >
        {megaNavLinks || showNewHeader ? (
          <BrandHeaderBody
            menuItems={megaNavLinks}
            brand={brand}
            onSignInAttempt={onSignInAttempt}
            onSignOut={onSignOut}
            wrapperId={wrapperId}
            user={user}
            loginOptions={loginOptions}
            languageSelectorOptions={languageSelectorOptions}
            isFluid={isFluid}
            suppressLogo={suppressLogo}
            shouldSuppressHonorsAccountLinks={shouldSuppressHonorsAccountLinks}
            shouldSuppressFindStay={shouldSuppressFindStay}
            shouldSuppressJoinAndSignIn={shouldSuppressJoinAndSignIn}
            shouldOpenAccountLinksInNewWindow={shouldOpenAccountLinksInNewWindow}
            shouldOpenJoinInNewWindow={shouldOpenJoinInNewWindow}
            shouldOpenJoinInPage={shouldOpenJoinInPage}
            heading={heading}
            mvts={mvts}
            headerVariantType={headerVariantType}
            surfaceType={surfaceType}
            surfaceClassName={rest.className}
            globalNavLinks={globalNavLinksFiltered}
            isTranslucent={isTranslucent}
            onGlobalNavigationClick={onGlobalNavigationClick}
            isLegacyGlobalNavigationClickAnalyticsEnabled={
              isLegacyGlobalNavigationClickAnalyticsEnabled
            }
            analytics={analytics}
          />
        ) : languageSelectorOptions ? (
          <NewHeaderBody
            {...commonHeaderBodyProps}
            defaultOptions={defaultOptions}
            languageSelectorOptions={languageSelectorOptions}
          />
        ) : (
          <OldHeaderBody {...commonHeaderBodyProps} defaultOptions={defaultOptions} />
        )}
      </header>
      {children}
      <div id="skipped" tabIndex={-1} />
    </>
  );
};

type BodyProps = Pick<
  HeaderProps,
  | 'brand'
  | 'isFluid'
  | 'mainNavLinks'
  | 'onSignOut'
  | 'suppressLogo'
  | 'user'
  | 'userLinks'
  | 'loginOptions'
  | 'heading'
  | 'shouldOpenJoinInPage'
> & {
  defaultOptions: Pick<
    React.ComponentProps<typeof LoginForm>,
    'onLoginAttempt' | 'sourceId' | 'analytics' | 'isLegacySignInAnalyticsEnabled'
  >;
  analytics?: HeaderProps['analytics'];
};

export function OldHeaderBody({
  brand,
  isFluid,
  mainNavLinks,
  onSignOut,
  suppressLogo,
  user,
  userLinks,
  loginOptions,
  defaultOptions,
  heading,
  shouldOpenJoinInPage,
  analytics,
}: BodyProps) {
  return (
    <div
      className={cx('flex flex-wrap items-center pt-2 lg:py-2', {
        'container-fluid': isFluid,
        container: !isFluid,
      })}
    >
      {suppressLogo ? null : <HeaderLogoLink surfaceType="base" brand={brand} heading={heading} />}
      {mainNavLinks?.length ? <MainNav links={mainNavLinks} suppressLogo={suppressLogo} /> : null}
      <UserNav
        user={user}
        userLinks={userLinks}
        onSignOut={onSignOut}
        loginOptions={loginOptions}
        defaultOptions={defaultOptions}
        shouldOpenJoinInPage={shouldOpenJoinInPage}
        analytics={analytics}
      />
    </div>
  );
}

export function NewHeaderBody({
  brand,
  isFluid,
  mainNavLinks,
  onSignOut,
  suppressLogo,
  user,
  userLinks,
  loginOptions,
  defaultOptions,
  languageSelectorOptions,
  heading,
  shouldOpenJoinInPage,
  analytics,
}: BodyProps & {
  languageSelectorOptions: NonNullable<HeaderProps['languageSelectorOptions']>;
}) {
  return (
    <div
      className={cx('flex flex-wrap pt-1.5', {
        'container-fluid': isFluid,
        container: !isFluid,
      })}
    >
      <div className="flex w-full flex-wrap items-center justify-between gap-x-5 gap-y-2 pb-2 sm:justify-end sm:gap-x-12 sm:pb-0">
        <LanguageSelector {...languageSelectorOptions} />
        <div className="flex flex-wrap justify-end gap-x-2 gap-y-1.5 sm:flex-nowrap sm:gap-x-3">
          <UserNav
            user={user}
            userLinks={userLinks}
            onSignOut={onSignOut}
            loginOptions={loginOptions}
            defaultOptions={defaultOptions}
            shouldOpenJoinInPage={shouldOpenJoinInPage}
            analytics={analytics}
          />
        </div>
      </div>
      <div className="w-full items-center lg:flex lg:pb-2">
        {suppressLogo ? null : <HeaderLogoLink brand={brand} heading={heading} />}
        {mainNavLinks?.length ? <MainNav links={mainNavLinks} suppressLogo={suppressLogo} /> : null}
      </div>
    </div>
  );
}

export function UserNav({
  user,
  userLinks,
  onSignOut,
  loginOptions,
  defaultOptions,
  shouldOpenJoinInPage,
  analytics,
}: Pick<
  BodyProps,
  | 'user'
  | 'userLinks'
  | 'onSignOut'
  | 'loginOptions'
  | 'defaultOptions'
  | 'shouldOpenJoinInPage'
  | 'analytics'
>) {
  const { i18n, t } = useTranslation(['osc-header', 'osc-login']);
  const { locale = 'en' } = useRouter();
  const formattedHonorsPointsTotal = getLocaleFormattedNumber(user?.honorsPoints, { locale });
  return (
    <nav aria-label="Hilton Honors" className="relative ms-auto">
      {user ? (
        <DropdownMenu.Root dir={i18n.dir()} modal={false}>
          <DropdownMenu.Trigger asChild>
            <button
              onClick={() => {
                sendInteractionReward();
                sendReward('profile-name-click');
              }}
              className="btn--lg btn-text-text flex items-center"
              type="button"
            >
              <span className="brand-wa:font-normal">
                {t('greetings', { username: user.name })}
              </span>
              <Icon name="user-circle" size="md" variant="solid" className="ms-2" />
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="end"
              className="absolute end-0 z-50 w-56 divide-y divide-surface rounded border border-solid border-surface bg-surface p-2 shadow-lg outline-none"
            >
              <div className="flex flex-col px-2 py-3">
                <p className="leading-tight">
                  <span className="block font-bold">{user.honorsTier}</span>
                  <span>
                    {t('points', {
                      count: user.honorsPoints,
                      points: formattedHonorsPointsTotal,
                    })}
                  </span>
                </p>
              </div>
              {userLinks?.accountLink?.url ? (
                <HeaderMenuLink
                  onClick={(e) => {
                    userLinks.accountLink?.onClick?.(e);
                    sendInteractionReward();
                  }}
                  {...userLinks.accountLink}
                  label={t('account')}
                />
              ) : null}
              {/* Optional user links */}
              {userLinks?.additionalLinks?.length
                ? userLinks.additionalLinks.map((link) => (
                    <HeaderMenuLink
                      onClick={(e) => {
                        link.onClick?.(e);
                        sendInteractionReward();
                      }}
                      key={link.label}
                      {...link}
                    />
                  ))
                : null}
              <button
                className={cx(
                  'w-full px-2 py-3 text-start text-surface hover:text-surface-muted highlighted:bg-surface-legacy'
                )}
                onClick={onSignOut}
                id="sign-out"
                type="button"
              >
                {t('signOut')}
              </button>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      ) : userLinks?.signInLink || userLinks?.signUpLink ? (
        <ul className="flex items-baseline divide-x divide-surface rtl:divide-x-reverse">
          {userLinks.signUpLink ? (
            <li className="px-2 first:ps-0 last:pe-0 sm:px-3">
              <Link
                {...userLinks.signUpLink}
                showNewWindowIcon={false}
                data-osc-product="header-signup-link"
                underline={false}
                className="btn--base sm:btn--lg btn-text-text flex items-center brand-wa:!font-normal sm:inline-flex"
                onClick={(e) => {
                  userLinks.signUpLink?.onClick?.(e);
                  sendReward('nav-join-click');
                  sendInteractionReward();
                }}
              >
                {t('signUp')}
              </Link>
            </li>
          ) : null}
          {userLinks.signInLink ? (
            <li className="px-2 first:ps-0 last:pe-0 sm:px-3">
              <NavItemWithModal
                onLoginAttempt={defaultOptions.onLoginAttempt}
                analytics={analytics}
                section="user"
                shouldOpenJoinInPage={shouldOpenJoinInPage}
                dialogTrigger={
                  <Button
                    fill="flat"
                    label={t('osc-login:signIn')}
                    className={cx(
                      'group font-semibold text-surface !no-underline brand-wa:!font-normal'
                    )}
                    iconProps={[
                      {
                        name: 'user-circle',
                        size: 'md',
                        variant: 'regular',
                        align: 'after',
                        className: 'group-hover:hidden group-focus:hidden',
                      },
                      {
                        name: 'user-circle',
                        size: 'md',
                        variant: 'solid',
                        align: 'after',
                        className: 'hidden group-hover:block group-focus:block',
                      },
                    ]}
                  />
                }
              >
                <LoginForm
                  onLoginAttempt={defaultOptions.onLoginAttempt}
                  {...loginOptions}
                  sourceId="OHW"
                  isInModal={true}
                />
              </NavItemWithModal>
            </li>
          ) : null}
        </ul>
      ) : null}
    </nav>
  );
}

export function MainNav({
  links,
  suppressLogo,
}: {
  links: LinkWithExperimentationConfiguration[];
  suppressLogo: HeaderProps['suppressLogo'];
}) {
  const navRef = React.useRef<React.ComponentRef<'div'>>(null);
  const [isBeforeVisible, setBeforeVisible] = React.useState(false);
  const [isAfterVisible, setAfterVisible] = React.useState(false);

  const onChange = React.useCallback((rect: DOMRect | undefined) => {
    const scrollLeft = navRef.current?.scrollLeft ?? 0;
    const scrollWidth = navRef.current?.scrollWidth ?? 0;
    const navWidth = rect?.width || 0;
    const widthDiff = scrollWidth - navWidth;
    const newBefore = scrollLeft > 5 && widthDiff > 0;
    const newAfter = widthDiff > 0 && widthDiff - scrollLeft > 5;
    setBeforeVisible(newBefore);
    setAfterVisible(newAfter);
  }, []);
  const navRect = useRect({ ref: navRef, onResize: onChange });
  const onNavScroll = React.useCallback(() => navRect && onChange(navRect), [onChange, navRect]);
  return (
    <nav
      className={cx(
        'relative order-last -mx-4 mt-2 overflow-hidden border-t border-solid border-surface px-4 py-2 flex-basis-full sm:-mx-8 sm:px-8 lg:order-none lg:mx-0 lg:mt-0 lg:grow lg:overflow-auto lg:border-t-0 lg:px-4 lg:flex-basis-auto',
        {
          'lg:ps-0': suppressLogo,
          'header-nav-before': isBeforeVisible,
          'header-nav-after': isAfterVisible,
        }
      )}
    >
      <div
        ref={navRef}
        onScroll={onNavScroll}
        className="w-full overflow-x-scroll py-1 lg:overflow-x-visible lg:py-0"
      >
        <ul className="flex flex-nowrap items-center space-x-6 rtl:space-x-reverse">
          {links.map(({ label, experimentationConfiguration, ...linkProps }) => (
            <li
              key={`main-nav-link-${label}`}
              className="text-surface focus-within:text-surface-muted hover:text-surface-muted"
            >
              <Link
                {...linkProps}
                underline={false}
                className="text--md whitespace-nowrap font-bold brand-wa:font-normal"
                onClick={(e) => {
                  linkProps.onClick?.(e);
                  sendInteractionReward();
                }}
                data-conductrics-goal={experimentationConfiguration?.goal}
                data-conductrics-value={experimentationConfiguration?.value}
              >
                {label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
}

export default Header;
